import { computed, ref } from 'vue'
import { useMessages } from '@/composables/useMessages'
import { NotifiedUser } from '@/@core/fire-risk-settings/domain/entities/NotifiedUser'
import { NotifiedUserTableColumnsFactory } from '../factories/TableColumns.factory'
import { INotifiedUserRepository } from '../../../../../../@core/fire-risk-settings/domain/repositories/INotifiedUser.repository'
import { CreateAndUpdateNotifiedUserDto } from '../../../../../../@core/fire-risk-settings/domain/dtos/CreateAndUpdateNotifiedUser.dto'
import { CreateNotifiedUserSchema } from '../../../../../../@core/fire-risk-settings/infra/notified-user-repository-adapter/schemas/CreateNotifiedUser.schema'
import { UpdateNotifieduserSchema } from '../../../../../../@core/fire-risk-settings/infra/notified-user-repository-adapter/schemas/UpdateNotifiedUser.schema'
import { NotificationChannelEnum } from '../../../../../../@core/fire-risk-settings/domain/enums/NotificationChannel.enum'
import { IMonitoredAreaRepository } from '../../../../../../@core/monitored-area/domain/repositories/IMonitoredArea.repository'
import { MonitoredArea } from '../../../../../../@core/monitored-area/domain/entities/MonitoredArea'
import { NotifiedUserFormSchema } from '../schemas/NotifiedUserForm.schema'
import { ExportDataProvider } from '../../../../@shared/providers/ExportData.provider'
import { useTable } from '../../../../../ui/components/baseTable/composables/Table.composable'

export class NotifiedUserViewModel {
	constructor(
		private _notifiedUserRepository: INotifiedUserRepository,
		private _monitoredAreaRepository: IMonitoredAreaRepository,
	) {}

	private _addMessage = useMessages().addMessage

	public openForm = ref(false)
	public fetching = ref(false)

	public notifiedUsers = ref<NotifiedUser[]>([])

	public showDeletePopup = ref(false)

	colunas = computed(() => NotifiedUserTableColumnsFactory.make(this.registry.sortOptions.value))

	public registry = useTable(this.notifiedUsers)

	createdUserId = ref()

	getNotifiedUsersToDelete() {
		if (!this.notifiedUsers.value) return []
		return this.notifiedUsers.value
			.filter((notifiedUser) =>
				this.registry.entriesToDelete.value.includes(notifiedUser.id as never),
			)
			.map((notifiedUser) => notifiedUser.name)
	}

	kebabOptions() {
		return [
			{
				icon: 'ic:round-edit',
				event: 'editar',
				name: 'Editar',
			},
		]
	}

	public farms = ref<MonitoredArea[]>([])

	public monitoredAreaOptions = computed(() =>
		this.farms.value.map((area) => {
			return { label: area.name, value: area.id }
		}),
	)

	private _initNotifiedUserState: NotifiedUser = {
		id: undefined,
		name: '',
		email: '',
		phoneNumber: '',
		channels: [],
		farms: [],
	}

	public notifiedUser = ref({
		...this._initNotifiedUserState,
	})

	showTelegramSection = computed(
		() =>
			this.notifiedUser.value.channels.includes(NotificationChannelEnum.TELEGRAM) &&
			this.notifiedUser.value.id,
	)

	_mapAlertSettingsToCreate(): CreateAndUpdateNotifiedUserDto | undefined {
		const result = CreateNotifiedUserSchema.safeParse(this.notifiedUser.value)

		const customerId = localStorage.getItem('customerId')
		if (!result.success) {
			this._onErrorCreateUpdateNotifiedUser(result.error.issues.at(0)!.message)
			return undefined
		}

		return { ...result.data, id_customer_smartforest: customerId! }
	}

	_mapAlertSettingsToUpdate(): CreateAndUpdateNotifiedUserDto | undefined {
		const result = UpdateNotifieduserSchema.safeParse(this.notifiedUser.value)

		if (!result.success) {
			this._onErrorCreateUpdateNotifiedUser(result.error.issues.at(0)!.message)
			return undefined
		}

		return result.data
	}

	async createNotifiedUser() {
		this.fetching.value = true
		try {
			const payload = this._mapAlertSettingsToCreate()
			if (!payload) return

			const id = await this._notifiedUserRepository.create(payload)

			if (this.notifiedUser.value.channels.includes(NotificationChannelEnum.TELEGRAM))
				this.createdUserId.value = id

			this._onSuccessCreateNotifiedUser()
		} catch (error) {
			this._onErrorCreateUpdateNotifiedUser((error as Error).message)
		} finally {
			this.fetching.value = false
		}
	}

	async updateNotifiedUser() {
		const customerId = localStorage.getItem('customerId')
		this.fetching.value = true
		try {
			const payload = this._mapAlertSettingsToUpdate()
			if (!payload) return

			await this._notifiedUserRepository.update({
				...payload,
				id_customer_smartforest: customerId!,
			})
			this._onSuccessUpdateNotifiedUser()
		} catch (error) {
			this._onErrorCreateUpdateNotifiedUser((error as Error).message)
		} finally {
			this.fetching.value = false
		}
	}

	loadNotifiedUser(notifiedUserId: string) {
		const notifiedUser = this.notifiedUsers.value?.find(
			(notifiedUser) => notifiedUser.id === notifiedUserId,
		)
		if (notifiedUser) {
			this.notifiedUser.value = { ...notifiedUser }
			this.openForm.value = true
		} else {
			this._addMessage({
				description: 'Erro ao recuperar dados de usuário notificado',
				type: 'error',
			})
		}
	}

	async getNotifiedUsers() {
		const customerId = localStorage.getItem('customerId')
		const response = await this._notifiedUserRepository.getAll(customerId!)
		this.notifiedUsers.value = response
	}

	async getMonitoredAreas() {
		try {
			const customerId = localStorage.getItem('customerId')
			const response = await this._monitoredAreaRepository.getSetted(customerId!)
			this.farms.value = response
		} catch {
			this._addMessage({ description: 'Erro ao buscar áreas monitoradas', type: 'error' })
		}
	}

	async deleteNotifiedUsers() {
		try {
			await this._notifiedUserRepository.delete(this.registry.entriesToDelete.value)

			this.fetching.value = false
			this.registry.entriesToDelete.value = []
			this.showDeletePopup.value = false
			this.getNotifiedUsers()
			this._addMessage({
				description: 'Usuário de alerta de incêndio excluído com sucesso!',
				type: 'info',
			})
		} catch {
			this._addMessage({ description: 'Erro ao excluir usuários notificados', type: 'error' })
		}
	}

	private _onSuccessUpdateNotifiedUser() {
		this.openForm.value = false
		this.getNotifiedUsers()
		this._addMessage({
			description: 'Usuário de alerta de incêndio editado com sucesso!',
			type: 'success',
		})
	}

	private _onSuccessCreateNotifiedUser() {
		this.openForm.value = false
		this.getNotifiedUsers()
		this._addMessage({
			description: 'Usuário de alerta de incêndio cadastrado com sucesso!',
			type: 'success',
		})
	}

	private _onErrorCreateUpdateNotifiedUser(errorMessage: string) {
		this._addMessage({
			description: errorMessage,
			type: 'error',
		})
	}

	public resetNotifiedUserState() {
		this.notifiedUser.value = { ...this._initNotifiedUserState }
	}

	isFormValid = computed(() => {
		const { success } = NotifiedUserFormSchema.safeParse(this.notifiedUser.value)
		return success
	})

	exportDataTable() {
		const columns = [
			{ title: 'Nome', key: 'name' },
			{ title: 'E-mail', key: 'email' },
			{ title: 'Telefone', key: 'phoneNumber' },
			{ title: 'Canal de Mensagens', key: 'channels' },
		]

		ExportDataProvider.downloadCSV(
			this.notifiedUsers.value!,
			columns,
			`Usuários Notificados ${new Date().toISOString()}`,
		)
	}
}
